'use client'

import { cn } from '@/lib/utils'
import { Typo3Image } from '@/types'
import { Button, Icon, Tooltip } from '@/ui/components'
import { BaseLinkProps } from '@/ui/components/types'
import { MEDIA } from '@spot'
import { memo } from 'react'
import BaseLink from '../Base/BaseLink'
import Title from '../Base/Title'
import Img from '../Img/Img'

export interface CardLinkProps extends Omit<BaseLinkProps, 'target'> {
  variant: 'shop' | 'content'
  extLink?: boolean
  title?: string
  showSubtitle?: boolean
  subTitle?: string
  showPrice?: boolean
  price?: string
  showTooltip?: boolean
  tooltipText?: string
  target?: string
  image?: Typo3Image
  showArrow?: boolean
  sizeSmall?: boolean
}

const CardLink = ({
  variant,
  title,
  tooltipText,
  price,
  showArrow = true,
  subTitle,
  className,
  image,
  target,
  sizeSmall,
  ...props
}: CardLinkProps) => {
  const classes = cn(
    'group aspect-rect relative grid h-full w-full grid-cols-1 grid-rows-[auto_1fr] p-md bg-shop-300 text-neutral-100',
    variant === 'content' && 'text-white',
    className
  )

  return (
    <BaseLink className={classes} {...props} target={target}>
      {variant === 'content' && image && (
        <div className="absolute inset-0 overflow-hidden">
          <Img
            data={image}
            className="image-hover-effect h-full w-full"
            objectCover
            fill
            naked
            data-image
            sizes={`${MEDIA.mobile} 100vw, ${MEDIA.tablet} 100vw, ${MEDIA.desktop} ${sizeSmall ? '43rem' : '87rem'}`}
          />
          <div className="absolute top-0 left-0 h-full w-full bg-[linear-gradient(180deg,rgb(0_0_0/0%)_41%,rgb(0_0_0/60%)_100%)]" />
        </div>
      )}
      <div className="grid grid-cols-[1fr_auto]">
        {price && (
          <div
            className={cn(
              'gap-sm flex items-center',
              variant === 'content' && image ? '' : ''
            )}
          >
            {tooltipText ? (
              <Tooltip>
                <Tooltip.Trigger>
                  <Button
                    variant="text"
                    icon="material-symbols:info"
                    iconPosition="right"
                    className="textstyle-lg-bold text-primary-100"
                  >
                    {price}
                  </Button>
                </Tooltip.Trigger>
                <Tooltip.Content>{tooltipText}</Tooltip.Content>
              </Tooltip>
            ) : (
              <p className={cn('textstyle-lg-bold')}>{price}</p>
            )}
          </div>
        )}
        {variant === 'shop' && (
          <div
            data-icon
            className={cn(
              'bg-accent-100 text-accent-300 absolute top-0 right-0 flex h-16 w-16 items-center justify-center justify-self-end'
            )}
          >
            {showArrow && (
              <Icon icon="material-symbols:arrow-outward" width={44} />
            )}
          </div>
        )}
      </div>

      <div className="relative flex flex-col justify-end" data-body>
        <Title as="h3" className="h3 line-clamp-3">
          {title}
        </Title>
        {subTitle && (
          <p
            className={cn(
              'text-shop-100 line-clamp-3',
              variant === 'content' && image ? '' : ''
            )}
          >
            {subTitle}
          </p>
        )}
      </div>
    </BaseLink>
  )
}

export default memo(CardLink)
